import React, {useEffect} from 'react'

import Link from 'next/link'
import classNames from 'classnames'
import {useMedia} from 'react-use'
import styled from 'styled-components'
import {media} from 'styled-bootstrap-grid'
import ArrowUpSLineIcon from 'remixicon-react/ArrowUpSLineIcon'
import ArrowDownSLineIcon from 'remixicon-react/ArrowDownSLineIcon'
import ArrowLeftRightLineIcon from 'remixicon-react/ArrowLeftRightLineIcon'

import {H6} from '@festi/common/components/typography'
import {MainButton} from '@festi/common/components/buttons'
import {Expandable} from '@festi/common/components/common'
import {useComparison} from '@festi/common/contexts'
import {gridTheme, styledTheme} from '@festi/common/themes'
import {useUserPrices} from '@festi/common/utils/rest'
import {setBodyScroll} from '@festi/common/utils/dom'

import CompareDrawerItem from './CompareDrawerItem'
import StickyMobileButtons from './StickyMobileButtons'
import CompareDrawerCountBubble from './CompareDrawerCountBubble'
import {useRouter} from 'next/router'

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${styledTheme.zIndex.modal};
  opacity: 0.6;
  background-color: ${styledTheme.palette.ui60};
  transition: all 300ms ease-in-out;

  &.scrollRowOpen {
    bottom: 72px;
  }

  &.hide {
    opacity: 0;
    pointer-events: none;
  }
`

const DrawerRoot = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: fit-content;
  max-height: 96dvh;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  box-shadow: ${styledTheme.boxShadow.simpleSubtle};
  background-color: ${styledTheme.palette.white};
  transform: translateY(100%);
  transition: 300ms;
  z-index: ${styledTheme.zIndex.compareDrawer};

  &.scrollRowOpen.expanded {
    bottom: 72px;
  }

  &.expanded {
    transform: translateY(0);
    z-index: ${styledTheme.zIndex.modal};
  }

  ${media.sm`
    max-width: 460px;
    left: 16px;

    &.expanded {
      z-index: ${styledTheme.zIndex.compareDrawer};
    }

    &.scrollRowOpen {
      bottom: 72px;
    }

    &.show {
      transform: translateY(0);
    }

    &.bounce {
      animation: bounce 300ms cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
    }
  `}

  ${media.md`
    &.scrollRowOpen.expanded,
    &.scrollRowOpen {
      bottom: 84px;
    }
  `}

  @keyframes bounce {
    0% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(5%);
    }
    100% {
      transform: translateY(0);
    }
  }
`

const ExpandButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding-block: 16px;
  padding-inline: 24px;
  background-color: ${styledTheme.palette.green};
  border: none;
  border-radius: inherit;
  cursor: pointer;
  user-select: none;

  & > div {
    display: flex;
    align-items: center;
    gap: 12px;
  }
`

const ButtonText = styled(H6)`
  font-size: 18px;
`

const ExpandableContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  overflow-y: auto;
`

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

export default function CompareDrawer() {
  const router = useRouter()

  const {
    isComparing,
    comparedProducts,
    compareDrawerBounce,
    compareDrawerExpanded,
    changeCompareDrawerPosition,
    removeFromComparison,
    setCompareDrawerExpanded,
  } = useComparison()

  const {data: userPrices} = useUserPrices(
    comparedProducts?.map((variant) => variant.sku),
  )

  const isSmallMobile = useMedia(
    `(max-width: ${gridTheme.breakpoints.sm}px)`,
    false,
  )

  const disableMobileTriggers =
    router.pathname.includes('leit') || router.pathname.includes('voruflokkar')

  useEffect(() => {
    if (isComparing && isSmallMobile && compareDrawerExpanded) {
      setBodyScroll(false)
    }
    return () => {
      setBodyScroll(true)
    }
  }, [isComparing, isSmallMobile, compareDrawerExpanded])

  return (
    <>
      <Overlay
        className={classNames({
          hide: !(compareDrawerExpanded && isComparing && isSmallMobile),
          scrollRowOpen: changeCompareDrawerPosition && isComparing,
        })}
        onClick={() => setCompareDrawerExpanded(false)}
      />

      <DrawerRoot
        className={classNames({
          show: isComparing,
          scrollRowOpen: changeCompareDrawerPosition && isComparing,
          expanded: compareDrawerExpanded && isComparing,
          bounce: compareDrawerBounce,
        })}
      >
        <ExpandButton
          onClick={() => setCompareDrawerExpanded(!compareDrawerExpanded)}
          aria-label="Toggle compare drawer"
        >
          <div>
            <CompareDrawerCountBubble count={comparedProducts?.length || 0} />
            <ButtonText>Samanburður</ButtonText>
          </div>
          {compareDrawerExpanded ? (
            <ArrowUpSLineIcon size={16} color={styledTheme.palette.blue} />
          ) : (
            <ArrowDownSLineIcon size={16} color={styledTheme.palette.blue} />
          )}
        </ExpandButton>

        <Expandable expanded={compareDrawerExpanded}>
          <ExpandableContent>
            <ItemList>
              {comparedProducts?.map((variant, i) => (
                <CompareDrawerItem
                  key={variant.sku}
                  variant={variant}
                  onClickRemove={removeFromComparison}
                  position={i + 1}
                  userPrice={userPrices[variant.sku]}
                />
              ))}
            </ItemList>

            <Link href="/samanburdur/" passHref>
              <MainButton
                label="Skoða samanburð"
                prefixIcon={ArrowLeftRightLineIcon}
                iconColor={styledTheme.palette.green}
                fullWidth
              />
            </Link>
          </ExpandableContent>
        </Expandable>
      </DrawerRoot>

      {!disableMobileTriggers && <StickyMobileButtons />}
    </>
  )
}
